import { useEffect, type FunctionComponent } from 'react'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { routerBack } from '@/src/common/utils/routerBack'
import { Linking } from 'react-native'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useNewestApplication } from '@/src/experiences/card-application/hooks/useNewestApplication'

export type Platform = NonNullable<
  NonNullable<ReturnType<typeof useNewestApplication>['data']>['newestApplication']
>['platform']

export interface ProvideCardPlatformOptions {
  /** non-card members will not have a credit card (and thus no application, platform, etc.) */
  allowNonCardMember?: boolean
}

export function provideCardPlatform<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ToWrapComponent extends (props: any) => ReturnType<FunctionComponent>,
>(
  ForPlatform: FunctionComponent<{
    platform?: Platform
    ToWrap: ToWrapComponent
    newestApplicationQuery: ReturnType<typeof useNewestApplication>
  }>,
  defaultOptions: ProvideCardPlatformOptions = {},
) {
  return (ToWrap: ToWrapComponent, { allowNonCardMember } = defaultOptions) => {
    const useHandleAbsentCardAccount = allowNonCardMember
      ? () => {}
      : (
          Alert: ReturnType<typeof useAlert>,
          logError: ReturnType<typeof useLogError>,
          query: ReturnType<typeof useNewestApplication>,
        ) =>
          useEffect(() => {
            if (!query.isUninitialized && !query.isLoading && !query.isFetching) {
              switch (query.data?.newestApplication?.status) {
                case 'ISSUED':
                case 'APPROVAL_ACKED':
                  break
                default:
                  logError(`Could not access card account`, query)
                  Alert.alert(
                    `Card account not found`,
                    'Please try again later or contact support@mesamember.com',
                    [
                      { text: 'OK', onPress: () => void routerBack() },
                      {
                        text: 'Contact support',
                        onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
                      },
                    ],
                    {
                      cancelable: false,
                      closeableByOverlay: false,
                      onDismiss: () => void routerBack(),
                    },
                  )
                  return
              }

              switch (query.data.newestApplication.platform) {
                case 'HIGHNOTE':
                case 'LOANPRO':
                  break
                default:
                  logError(
                    `Virtual card attempted to be accessed when no valid card application platform was found`,
                    query,
                  )
                  Alert.alert(
                    `Card account not found`,
                    'Please try again later or contact support@mesamember.com',
                  )
              }
            }
          }, [query, Alert, logError])

    return function Wrapped() {
      const Alert = useAlert(provideCardPlatform.name)
      const logError = useLogError()
      const query = useNewestApplication()

      useHandleAbsentCardAccount(Alert, logError, query)

      switch (query.data?.newestApplication?.status) {
        case 'ISSUED':
        case 'APPROVAL_ACKED':
          break
        default:
          return <ForPlatform newestApplicationQuery={query} ToWrap={ToWrap} />
      }

      return (
        <ForPlatform
          newestApplicationQuery={query}
          platform={query.data.newestApplication.platform}
          ToWrap={ToWrap}
        />
      )
    }
  }
}
